/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau
 */

import { getAccountsUrl, getApiGatewayUrl, getViewerIframeUrl } from ".";

enum EApiRoute {
    AccountsApi = "api",
    AccountsApiToken = "token",
    AccountsAuthorize = "authorize",
    AccountsAuthorizeConsent = "consent",
    AccountsLogin = "login",
    AccountsLogout = "logout",
    AccountsPassword = "password",
    AccountsStatus = "status",
    Auth = "auth",
    AuthToken = "token",
    AuthTokenRevoke = "revoke",
    Boards = "boards",
    BoardGroups = "groups",
    BoardItems = "items",
    BoardProperties = "properties",
    BoardSections = "sections",
    BoardCells = "cells",
    Files = "files",
    FileUpload = "upload",
    Me = "@me",
    Modelfiles = "modelfiles",
    Models = "models",
    Quantities = "quantities",
    Status = "status",
    Versions = "versions",
    Workspaces = "workspaces"
}

const _AccountsLocalUrl: string = "http://localhost:5500";
const _ViewerLocalUrl: string = "http://localhost:5000";
const _AuthApiLocalUrl: string = "http://localhost:5001";
const _WorkspacesApiLocalUrl: string = "http://localhost:5002";
const _ModelsApiLocalUrl: string = "http://localhost:5003";
const _FilesApiLocalUrl: string = "http://localhost:5004";
const _QuantitiesApiLocalUrl: string = "http://localhost:5005";
const _BoardsApiLocalUrl: string = "http://localhost:5006";

export function FormatApiRoutes(cloudRunEnabled: boolean, apiVersion: string): string[] {
    return [
        FormatViewerUrl(cloudRunEnabled),
        FormatAccountsUrl(cloudRunEnabled),
        FormatAccountsLoginPasswordUrl(cloudRunEnabled),
        FormatAccountsAuthorizeUrl(cloudRunEnabled),
        FormatAccountsAuthorizeConsentUrl(cloudRunEnabled),
        FormatAccountsStatusUrl(cloudRunEnabled),
        FormatAccountsLogoutUrl(cloudRunEnabled),
        FormatAccountsApiTokenUrl(cloudRunEnabled),
        FormatApiGatewayUrl(cloudRunEnabled),
        FormatAuthTokenUrl(cloudRunEnabled, apiVersion),
        FormatAuthTokenRevokeUrl(cloudRunEnabled, apiVersion),
        FormatMeWorkspacesUrl(cloudRunEnabled, apiVersion),
        FormatMeWorkspaceUrl(cloudRunEnabled, apiVersion),
        FormatMeModelsUrl(cloudRunEnabled, apiVersion),
        FormatMeModelUrl(cloudRunEnabled, apiVersion),
        FormatMeModelfilesUrl(cloudRunEnabled, apiVersion),
        FormatMeModelfileUrl(cloudRunEnabled, apiVersion),
        FormatMeVersionsUrl(cloudRunEnabled, apiVersion),
        FormatMeVersionUrl(cloudRunEnabled, apiVersion),
        FormatMeVersionStatusUrl(cloudRunEnabled, apiVersion),
        FormatMeWorkspaceVersionsUrl(cloudRunEnabled, apiVersion),
        FormatMeWorkspaceVersionUrl(cloudRunEnabled, apiVersion),
        FormatMeWorkspaceVersionFilesUrl(cloudRunEnabled, apiVersion),
        FormatFilesUploadUrl(cloudRunEnabled, apiVersion),
        FormatMeQuantitiesUrl(cloudRunEnabled, apiVersion),
        FormatMeQuantityUrl(cloudRunEnabled, apiVersion),
        FormatMeQuantityFilesUrl(cloudRunEnabled, apiVersion),
        FormatMeBoardsUrl(cloudRunEnabled, apiVersion),
        FormatMeBoardUrl(cloudRunEnabled, apiVersion),
        FormatMeBoardStatusUrl(cloudRunEnabled, apiVersion),
        FormatMeBoardPropertiesUrl(cloudRunEnabled, apiVersion),
        FormatMeBoardSectionsUrl(cloudRunEnabled, apiVersion),
        FormatMeBoardSectionGroupsUrl(cloudRunEnabled, apiVersion),
        FormatMeBoardSectionGroupItemsUrl(cloudRunEnabled, apiVersion),
        FormatMeBoardCellsUrl(cloudRunEnabled, apiVersion),
    ];
}

export function FormatApiGatewayUrl(cloudRunEnabled: boolean): string {
    return cloudRunEnabled ? getApiGatewayUrl() : "";
}
export function FormatAccountsUrl(cloudRunEnabled: boolean): string {
    return cloudRunEnabled ? getAccountsUrl() : _AccountsLocalUrl;
}
export function FormatViewerUrl(cloudRunEnabled: boolean, addCloudRunEnabledUrlParam: boolean = true): string {
    let oViewerUrl : string = cloudRunEnabled ? getViewerIframeUrl() : _ViewerLocalUrl;

    if( addCloudRunEnabledUrlParam ) {
        const cloudRunEnabledUrlParam: string = cloudRunEnabled ? "cloudRun" : "noCloudRun";
        oViewerUrl += "?" + cloudRunEnabledUrlParam;
    }
    
    return oViewerUrl;
}
export function FormatAuthTokenUrl(cloudRunEnabled: boolean, apiVersion: string): string {
    return _FormatApiRoute(cloudRunEnabled, apiVersion, EApiRoute.Auth + "/" + EApiRoute.AuthToken);
}
export function FormatAuthTokenRevokeUrl(cloudRunEnabled: boolean, apiVersion: string): string {
    return _FormatApiRoute(cloudRunEnabled, apiVersion, EApiRoute.Auth + "/" + EApiRoute.AuthToken + "/" + EApiRoute.AuthTokenRevoke);
}
export function FormatMeWorkspacesUrl(cloudRunEnabled: boolean, apiVersion: string): string {
    return _FormatApiMeRoute(cloudRunEnabled, apiVersion, EApiRoute.Workspaces);
}
export function FormatMeWorkspaceUrl(cloudRunEnabled: boolean, apiVersion: string, workspaceId: string = ":workspaceId"): string {
    return FormatMeWorkspacesUrl(cloudRunEnabled, apiVersion) + `/${workspaceId}`;
}
export function FormatMeModelsUrl(cloudRunEnabled: boolean, apiVersion: string, workspaceId: string = ":workspaceId"): string {
    return _FormatApiMeRoute(cloudRunEnabled, apiVersion, EApiRoute.Workspaces + `/${workspaceId}/` + EApiRoute.Models);
}
export function FormatMeModelUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    modelId: string = ":modelId"
): string {
    return FormatMeModelsUrl(cloudRunEnabled, apiVersion, workspaceId) + `/${modelId}`;
}
export function FormatMeModelfilesUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    modelId: string = ":modelId"
): string {
    return _FormatApiMeRoute(
        cloudRunEnabled,
        apiVersion,
        EApiRoute.Workspaces + `/${workspaceId}/` + EApiRoute.Models + `/${modelId}/` + EApiRoute.Modelfiles
    );
}
export function FormatMeModelfileUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    modelId: string = ":modelId",
    modelfileId: string = ":modelfileId"
): string {
    return FormatMeModelfilesUrl(cloudRunEnabled, apiVersion, workspaceId, modelId) + `/${modelfileId}`;
}
export function FormatMeVersionsUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    modelId: string = ":modelId",
    modelfileId: string = ":modelfileId"
): string {
    return _FormatApiMeRoute(
        cloudRunEnabled,
        apiVersion,
        EApiRoute.Workspaces + `/${workspaceId}/` + EApiRoute.Models + `/${modelId}/` + EApiRoute.Modelfiles + `/${modelfileId}/` + EApiRoute.Versions
    );
}
export function FormatMeVersionUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    modelId: string = ":modelId",
    modelfileId: string = ":modelfileId",
    versionId: string = ":versionId"
): string {
    return FormatMeVersionsUrl(cloudRunEnabled, apiVersion, workspaceId, modelId, modelfileId) + `/${versionId}`;
}
export function FormatMeVersionStatusUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    modelId: string = ":modelId",
    modelfileId: string = ":modelfileId",
    versionId: string = ":versionId"
) {
    return FormatMeVersionUrl(cloudRunEnabled, apiVersion, workspaceId, modelId, modelfileId, versionId) + "/" + EApiRoute.Status;
}
export function FormatMeWorkspaceVersionsUrl(cloudRunEnabled: boolean, apiVersion: string, workspaceId: string = ":workspaceId"): string {
    return _FormatApiMeRoute(cloudRunEnabled, apiVersion, EApiRoute.Workspaces + `/${workspaceId}/` + EApiRoute.Versions);
}
export function FormatMeWorkspaceVersionUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    versionId: string = ":versionId"
): string {
    return FormatMeWorkspaceVersionsUrl(cloudRunEnabled, apiVersion, workspaceId) + `/${versionId}`;
}
export function FormatMeWorkspaceVersionFilesUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    versionId: string = ":versionId"
): string {
    return FormatMeWorkspaceVersionUrl(cloudRunEnabled, apiVersion, workspaceId, versionId) + "/" + EApiRoute.Files;
}
export function FormatMeQuantitiesUrl(cloudRunEnabled: boolean, apiVersion: string, workspaceId: string = ":workspaceId"): string {
    return _FormatApiMeRoute(cloudRunEnabled, apiVersion, EApiRoute.Workspaces + `/${workspaceId}/` + EApiRoute.Quantities);
}
export function FormatMeQuantityUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    quantityId: string = ":quantityId"
): string {
    return FormatMeQuantitiesUrl(cloudRunEnabled, apiVersion, workspaceId) + `/${quantityId}`;
}
export function FormatMeQuantityFilesUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    quantityId: string = ":quantityId"
): string {
    return FormatMeQuantityUrl(cloudRunEnabled, apiVersion, workspaceId, quantityId) + `/${EApiRoute.Files}`;
}
export function FormatFilesUploadUrl(cloudRunEnabled: boolean, apiVersion: string): string {
    return _FormatApiRoute(cloudRunEnabled, apiVersion, EApiRoute.Files + "/" + EApiRoute.FileUpload);
}
export function FormatMeBoardsUrl(cloudRunEnabled: boolean, apiVersion: string, workspaceId: string = ":workspaceId"): string {
    return _FormatApiMeRoute(cloudRunEnabled, apiVersion, EApiRoute.Workspaces + `/${workspaceId}/` + EApiRoute.Boards);
}
export function FormatMeBoardUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    boardId: string = ":boardId"
): string {
    return FormatMeBoardsUrl(cloudRunEnabled, apiVersion, workspaceId) + `/${boardId}`;
}
export function FormatMeBoardStatusUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    boardId: string = ":boardId"
): string {
    return FormatMeBoardUrl(cloudRunEnabled, apiVersion, workspaceId, boardId) + `/${EApiRoute.Status}`;
}
export function FormatMeBoardSectionsUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    boardId: string = ":boardId"
): string {
    return FormatMeBoardUrl(cloudRunEnabled, apiVersion, workspaceId, boardId) + `/${EApiRoute.BoardSections}`;
}
export function FormatMeBoardSectionUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    boardId: string = ":boardId",
    sectionId: string = ":sectionId"
) : string {
    return FormatMeBoardSectionsUrl(cloudRunEnabled, apiVersion, workspaceId, boardId) + `/${sectionId}`;
}
export function FormatMeBoardSectionGroupsUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    boardId: string = ":boardId",
    sectionId: string = ":sectionId"
): string {
    return FormatMeBoardSectionUrl(cloudRunEnabled, apiVersion, workspaceId, boardId, sectionId) + `/${EApiRoute.BoardGroups}`;
}
export function FormatMeBoardSectionGroupUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    boardId: string = ":boardId",
    sectionId: string = ":sectionId",
    groupId: string = ":groupId"
): string {
    return FormatMeBoardSectionGroupsUrl(cloudRunEnabled, apiVersion, workspaceId, boardId, sectionId) + `/${groupId}`;
}
export function FormatMeBoardSectionGroupItemsUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    boardId: string = ":boardId",
    sectionId: string = ":sectionId",
    groupId: string = ":groupId"
): string {
    return FormatMeBoardSectionGroupUrl(cloudRunEnabled, apiVersion, workspaceId, boardId, sectionId, groupId) + `/${EApiRoute.BoardItems}`;
}
export function FormatMeBoardPropertiesUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    boardId: string = ":boardId"
): string {
    return FormatMeBoardUrl(cloudRunEnabled, apiVersion, workspaceId, boardId) + `/${EApiRoute.BoardProperties}`;
}
export function FormatMeBoardCellsUrl(
    cloudRunEnabled: boolean,
    apiVersion: string,
    workspaceId: string = ":workspaceId",
    boardId: string = ":boardId"
): string {
    return FormatMeBoardUrl(cloudRunEnabled, apiVersion, workspaceId, boardId) + `/${EApiRoute.BoardCells}`;
}
export function FormatAccountsLoginPasswordUrl(cloudRunEnabled: boolean): string {
    return _FormatAccountsRoute(cloudRunEnabled, EApiRoute.AccountsLogin + "/" + EApiRoute.AccountsPassword);
}
export function FormatAccountsAuthorizeUrl(cloudRunEnabled: boolean): string {
    return _FormatAccountsRoute(cloudRunEnabled, EApiRoute.AccountsAuthorize);
}
export function FormatAccountsAuthorizeConsentUrl(cloudRunEnabled: boolean): string {
    return FormatAccountsAuthorizeUrl(cloudRunEnabled) + "/" + EApiRoute.AccountsAuthorizeConsent;
}
export function FormatAccountsStatusUrl(cloudRunEnabled: boolean): string {
    let url : string = _FormatAccountsRoute(cloudRunEnabled, EApiRoute.AccountsStatus);

    if( !cloudRunEnabled ) {
        url += `?noCloudRun=1`
    }

    return url;
} 
export function FormatAccountsLogoutUrl(cloudRunEnabled: boolean): string {
    let url : string = _FormatAccountsRoute(cloudRunEnabled, EApiRoute.AccountsLogout);

    if( !cloudRunEnabled ) {
        url += `?noCloudRun=1`
    }

    return url;
}
export function FormatAccountsApiUrl(cloudRunEnabled: boolean): string {
    return _FormatAccountsRoute(cloudRunEnabled, EApiRoute.AccountsApi);
}
export function FormatAccountsApiTokenUrl(cloudRunEnabled: boolean) {
    return FormatAccountsApiUrl(cloudRunEnabled) + "/" + EApiRoute.AccountsApiToken;
}


function _FormatApiBaseRoute(cloudRunEnabled: boolean, apiVersion: string, apiRoute: string): string {
    let apiUrl: string = "";

    if (!cloudRunEnabled) {
        const endpoints: string[] = apiRoute.split("/");
        const [firstEndpoint]: string[] = endpoints;
        if (EApiRoute.Auth === firstEndpoint ) {
            apiUrl = _AuthApiLocalUrl;
        } else if (EApiRoute.Files === firstEndpoint) {
            apiUrl = _FilesApiLocalUrl;
        } else if (EApiRoute.Workspaces === firstEndpoint) {
            if (endpoints.length > 2) {
                const [, , secondEndpoint] = endpoints;
                if (EApiRoute.Models === secondEndpoint || EApiRoute.Versions === secondEndpoint) {
                    apiUrl = _ModelsApiLocalUrl;
                } else if ( EApiRoute.Quantities === secondEndpoint ) {
                    apiUrl = _QuantitiesApiLocalUrl;
                } else if( EApiRoute.Boards === secondEndpoint ) {
                    apiUrl = _BoardsApiLocalUrl;
                } else {
                    console.error( `ApiWeb::RouteFormatter::_FormatApiBaseRoute(): Can't format api route: ${apiRoute}` );
                }
            } else {
                apiUrl = _WorkspacesApiLocalUrl;
            }
        }
    } else {
        apiUrl = FormatApiGatewayUrl(cloudRunEnabled);
    }

    return apiUrl + "/" + apiVersion;
}
function _FormatApiRoute(cloudRunEnabled: boolean, apiVersion: string, apiRoute: string): string {
    return _FormatApiBaseRoute(cloudRunEnabled, apiVersion, apiRoute) + "/" + apiRoute;
}
function _FormatApiMeRoute(cloudRunEnabled: boolean, apiVersion: string, apiRoute: string): string {
    return _FormatApiBaseRoute(cloudRunEnabled, apiVersion, apiRoute) + "/" + EApiRoute.Me + "/" + apiRoute;
}
function _FormatAccountsBaseRoute(cloudRunEnabled: boolean): string {
    let url: string = "";

    if (!cloudRunEnabled) {
        url = _AccountsLocalUrl;
    } else {
        url = FormatAccountsUrl(cloudRunEnabled);
    }

    return url + "/";
}
function _FormatAccountsRoute(cloudRunEnabled: boolean, route: string): string {
    return _FormatAccountsBaseRoute(cloudRunEnabled) + route;
}